import L, { LatLng, LeafletMouseEvent, PathOptions, PolylineOptions } from "leaflet";
import { useEffect, useContext, useRef, useState } from "react";
import { Polygon, useMapEvents } from "react-leaflet";
import Bounds from "../draw-tools/Bounds";
import ImageOverlayRotated from "../ImageOverlayRotated";
import { ToolContext } from "../../context/ToolContext";
import * as tab from "../../constants/tabs";
import { SitePlan, useSitePlan } from "./UseSitePlan";

// Extend MouseEvent to include clickedCableOnCanvas
interface CustomMouseEvent extends MouseEvent {
  clickedCableOnCanvas?: boolean;
}

interface SitePlanImportProps {
  sitePlan: SitePlan;
  sitePlanSelected: boolean;
  selectSitePlan: () => void;
}

const SitePlanPolygon: React.FC<SitePlanImportProps> = ({
  sitePlan,
  sitePlanSelected,
  selectSitePlan,
}) => {
  const { setSitePlanPositions, removeTransformPathLayers } = useSitePlan();
  const { toolState, setToolState } = useContext(ToolContext);
  const [isDragging, setIsDragging] = useState(false);
  const [isScaling, setIsScaling] = useState(false);
  const [fallbackGeometry, setFallbackGeometry] = useState<LatLng[] | null>(null);

  const resizeablePolygonRef = useRef<L.Polygon | null>(null);
  const imageOverlayRef = useRef<{
    options: { opacity: number };
    setStyle: (style: { opacity: number }) => void;
    reposition: (
      topLeft: [number, number],
      topRight: [number, number],
      bottomLeft: [number, number],
    ) => void;
    _topLeft: L.LatLng;
    _bottomLeft: L.LatLng;
    _topRight: L.LatLng;
  } | null>(null);

  // This is to cleanup the layers that may be orphaned when the key changes for the polygon or the overlay
  useEffect(() => {
    removeTransformPathLayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const resizeablePolygon = resizeablePolygonRef.current;
    if (resizeablePolygon != null && resizeablePolygon.transform) {
      resizeablePolygon.transform.enable();
      sitePlan.lockSitePlanPosition
        ? resizeablePolygon.dragging.disable()
        : resizeablePolygon.dragging.enable();

      resizeablePolygon.transform.setOptions({
        uniformScaling: sitePlan.uniformScaling,
        rotation: !sitePlan.lockSitePlanPosition,
        scaling: !sitePlan.lockSitePlanPosition,

        // edge handlers
        handlerOptions: {
          radius: 6,
          fillColor: "#ffffff",
          color: "#3388ff",
          fillOpacity: 1,
          weight: 2,
          opacity: 0.7,
          setCursor: true,
          renderer: L.svg(),
        } as PathOptions,

        // rectangle
        boundsOptions: {
          weight: 2,
          opacity: 1,
          dashArray: [6, 6],
          fill: false,
          noClip: true,
          renderer: L.svg(),
        } as PolylineOptions,

        // rotation handler
        rotateHandleOptions: {
          weight: 1,
          opacity: 1,
          setCursor: true,
          renderer: L.svg(),
        } as PolylineOptions,
      });
      resizeablePolygon.setLatLngs(sitePlan.positions);
    }

    if (resizeablePolygon) {
      resizeablePolygon.setLatLngs([sitePlan.positions]);
      resizeablePolygon.fire("transformed", {
        target: resizeablePolygon,
        type: "transformed",
      });
    }
  }, [
    resizeablePolygonRef,
    sitePlan.lockSitePlanPosition,
    sitePlan.positions,
    sitePlan.uniformScaling,
    sitePlan.opacity,
    sitePlan.width,
    sitePlan.scaleLength,
  ]);

  const revertGeometryIfInvalid = (): boolean => {
    if (fallbackGeometry) {
      const polygon = resizeablePolygonRef.current;
      const positions = resizeablePolygonRef.current?.getLatLngs() as LatLng[][];
      if (!!polygon && !!positions && positions.length >= 1 && positions[0].length >= 4) {
        const startBounds = new Bounds(fallbackGeometry);
        const endBounds = new Bounds(positions[0]);
        if (!endBounds.isSame(startBounds)) {
          if (endBounds.width() < 1 || endBounds.height() < 1) {
            positions[0] = fallbackGeometry;
            // Make a tiny change to force Leaflet to recognize the geometry has changed
            positions[0][0].lat += 0.000000001;

            polygon.fire("transformed", {
              target: polygon,
              type: "transformed",
            });
            return true;
          }
        }
      }
    }
    return false;
  };

  useMapEvents({
    mousedown(e) {
      const clickedElement = e.originalEvent.target as HTMLElement;
      const className =
        clickedElement instanceof SVGElement
          ? clickedElement.className.baseVal
          : clickedElement.className;
      if (className.includes("leaflet-drag-transform-marker")) {
        const polygon = resizeablePolygonRef.current;
        const positions = resizeablePolygonRef.current?.getLatLngs() as LatLng[][];
        if (!!polygon && !!positions && positions.length >= 1 && positions[0].length >= 4) {
          setFallbackGeometry(positions[0].map((g) => new L.LatLng(g.lat, g.lng)));
          setIsScaling(true);
        }
      }
    },
    mouseup(e) {
      if (isScaling && fallbackGeometry) {
        revertGeometryIfInvalid();
        setIsScaling(false);
      }
    },
    keydown(e) {
      // Ignore Escape key to prevent errors when cancelling drag
      if (isDragging && e.originalEvent.key === "Escape") {
        const polygon = resizeablePolygonRef.current;
        if (polygon && polygon.dragging && (polygon.dragging as any)._enabled) {
          e.originalEvent.stopPropagation();
          e.originalEvent.preventDefault();
        }
      }
    },
  });

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      try {
        if (revertGeometryIfInvalid()) {
          setIsScaling(false);
        }
      } catch {}
    };

    window.addEventListener("error", handleError);
    return () => window.removeEventListener("error", handleError);
  }, [isScaling, fallbackGeometry]);

  return (
    <>
      <Polygon
        // The key forces the rerender of the polygon - now includes zoom level
        key={`${JSON.stringify(sitePlan.positions)}`}
        ref={resizeablePolygonRef}
        positions={sitePlan.positions}
        transform={sitePlanSelected}
        draggable={sitePlanSelected}
        pane={"siteplan-polygon-pane"}
        pathOptions={{
          weight: 2,
          dashArray: "6, 6",
          opacity: isDragging ? 1 : 0,
          fillOpacity: 0,
        }}
        eventHandlers={{
          click: (e: LeafletMouseEvent) => {
            // When a cable crosses the siteplan, select the cable rather than the siteplan
            // SVG cables automatically receive the click event as they have a higher zIndex than the siteplan
            // For Canvas cables, need to pass the click event through to the canvas to test if any cables get clicked

            const polygonPane = toolState.leafletMap?.getPane("siteplan-polygon-pane");
            if (!polygonPane) return;
            const prevDisplay = polygonPane.style.display;
            polygonPane.style.display = "none";
            let clickedCable = false;

            try {
              const target = document.elementFromPoint(
                e.originalEvent.clientX,
                e.originalEvent.clientY,
              );

              if (target && target !== polygonPane) {
                // Create a new MouseEvent and cast it to CustomMouseEvent
                const mouseEvent = new MouseEvent(e.originalEvent.type, {
                  bubbles: true,
                  cancelable: true,
                  clientX: e.originalEvent.clientX,
                  clientY: e.originalEvent.clientY,
                }) as CustomMouseEvent;

                mouseEvent.clickedCableOnCanvas = false; // Add the custom property
                target.dispatchEvent(mouseEvent); // Dispatch the custom event
                clickedCable = mouseEvent.clickedCableOnCanvas ?? false; // Read the custom property
              }
            } finally {
              polygonPane.style.display = prevDisplay;
            }

            if (!clickedCable) {
              // Select the site plan
              setToolState({ ...toolState, activeTab: tab.PROPERTIES });
              selectSitePlan();
            }
          },
          transformed: (e) => {
            const imageOverlay = imageOverlayRef.current;

            if (imageOverlay === null) return;

            const newPositions: [number, number][][] = (e.target.getLatLngs() as LatLng[][]).map(
              (m) => m.map((n) => [n.lat, n.lng]),
            );

            imageOverlay.reposition(newPositions[0][0], newPositions[0][1], newPositions[0][3]);

            if (JSON.stringify(newPositions) !== JSON.stringify([sitePlan.positions]))
              setSitePlanPositions(newPositions);
          },
          dragstart: () => {
            const polygon = resizeablePolygonRef.current;
            if (polygon) {
              polygon.setStyle({
                opacity: 1,
              });
            }
            setIsDragging(true);
          },
          dragend: () => {
            const polygon = resizeablePolygonRef.current;
            if (polygon) {
              polygon.setStyle({
                opacity: 0,
              });
            }
            setIsDragging(false);
          },
        }}
      />

      <ImageOverlayRotated
        // The key forces the rerender when the opacity changes which causes a flickering effect
        // If a better way is apparent, please use that
        key={sitePlan.opacity}
        ref={imageOverlayRef}
        url={sitePlan.image}
        topLeft={sitePlan.positions[0]}
        topRight={sitePlan.positions[1]}
        bottomLeft={sitePlan.positions[3]}
        opacity={sitePlan.opacity}
        interactive={true}
      />
    </>
  );
};

export default SitePlanPolygon;
